import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { getTests, getRetiredCount, getDailyTargets } from '../../api';
import { useAuth } from '../../context/AuthContext';

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: tests = [], isLoading } = useQuery({ queryKey: ['tests'], queryFn: () => getTests().then(r => r.data) });
  const { data: retired } = useQuery({ queryKey: ['retiredCount'], queryFn: () => getRetiredCount().then(r => r.data) });
  const { data: dt, isLoading: dtLoading } = useQuery({ queryKey: ['dailyTargets'], queryFn: () => getDailyTargets().then(r => r.data) });
  const targets = dt?.targets || [];
  const targetsDone = targets.filter(t => t.practiced).length;

  const completed = tests.filter(t => t.status === 'completed');
  const recent = completed.slice(0, 5);

  return (
    <div>
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 28 }}>Welcome back, {user?.full_name?.split(' ')[0] || 'Student'}! 👋</h1>
        <p style={{ color: 'var(--text-muted)', marginTop: 4 }}>Ready to ace NEET? Let's get started.</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 32 }}>
        {[
          { label: 'Tests Taken', value: completed.length, icon: '📝', color: '#EEF2FF' },
          { label: 'In Progress', value: tests.filter(t=>t.status==='in_progress').length, icon: '⏳', color: '#FFF7ED' },
          { label: 'Retired', value: retired?.count ?? 0, icon: '🎓', color: '#ECFDF5', onClick: (retired?.count ?? 0) > 0 ? () => navigate('/student/test/new') : null },
        ].map(s => (
          <div key={s.label} className="card" onClick={s.onClick || undefined} style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: s.onClick ? 'pointer' : 'default' }} title={s.label === 'Retired' ? 'Questions you have retired (mastered). Tap to take a Retired Questions Test.' : undefined}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: s.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>{s.icon}</div>
            <div>
              <div style={{ fontSize: 26, fontWeight: 700 }}>{s.value}</div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ marginBottom: 24, borderTop: '3px solid var(--primary)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <h2 style={{ fontSize: 18, display: 'flex', alignItems: 'center', gap: 8 }}>🎯 Today's Target Modules
            {targets.length > 0 && <span className={`badge ${targetsDone === targets.length ? 'badge-green' : 'badge-yellow'}`}>{targetsDone}/{targets.length} done</span>}
          </h2>
          <span style={{ color: 'var(--primary)', fontSize: 13, cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate('/student/daily-target')}>Open Daily Target →</span>
        </div>
        {dtLoading ? <div className="spinner"></div> : targets.length === 0 ? (
          <div className="empty-state" style={{ padding: '20px 0' }}>🎉 No weak modules to practise right now. Great job!</div>
        ) : targets.map(t => (
          <div key={t.module_code} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary-light)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, flexShrink: 0 }}>{t.rank}</div>
              <div>
                <div style={{ fontWeight: 600 }}>{t.module_name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>Class {t.class} · {t.error_points} error point{t.error_points === 1 ? '' : 's'}</div>
              </div>
            </div>
            {t.practiced
              ? <span className="badge badge-green" style={{ flexShrink: 0 }}>Done · {t.score_pct != null ? Math.round(t.score_pct) + '%' : '—'}</span>
              : <button className="btn btn-primary btn-sm" style={{ flexShrink: 0 }} onClick={() => navigate('/student/daily-target')}>Practise Now</button>}
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 24 }}>
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/student/test/new')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <div style={{ width: 56, height: 56, borderRadius: 14, background: 'linear-gradient(135deg, var(--primary), var(--accent))',
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26 }}>📝</div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 18 }}>Start New Test</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Build a custom MCQ test</div>
            </div>
            <span style={{ marginLeft: 'auto', fontSize: 22, color: 'var(--text-muted)' }}>→</span>
          </div>
        </div>
        <div className="card" style={{ cursor: 'pointer' }} onClick={() => navigate('/student/cards')}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 48, height: 48, borderRadius: 12, background: '#FDF4FF', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🃏</div>
            <div>
              <div style={{ fontWeight: 600 }}>Flashcards</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 13 }}>Quick revision</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 18, marginBottom: 20 }}>Recent Tests</h2>
        {isLoading ? <div className="spinner"></div> : recent.length === 0 ? (
          <div className="empty-state" style={{ padding: '30px 0' }}>No tests yet. Start your first test above!</div>
        ) : (
          <table className="table">
            <thead><tr><th>Test</th><th>Questions</th><th>Date</th><th></th></tr></thead>
            <tbody>
              {recent.map(t => (
                <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/student/test/${t.id}/result`)}>
                  <td style={{ fontWeight: 500 }}>{t.title || 'Test'}</td>
                  <td>{t.total_questions}</td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 13 }}>{new Date(t.started_at).toLocaleDateString()}</td>
                  <td style={{ color: 'var(--primary)', fontSize: 13 }}>View Result →</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
